import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

/**
 * applyServerErrors — shared server-error -> react-hook-form field mapping
 * (form-standard.context.md §5 / Doc 69 `A-` rows).
 *
 * Consumes the shape thrown by `fetchWithAuth` (`src/services/api.ts`) on a non-2xx response: an
 * `Error` carrying `.details` (camelCase OR PascalCase field key -> message[] | message — some
 * controllers serialize the whole `Result<T>` envelope without a camelCase JSON contract resolver;
 * `api.ts` reads both casings defensively). Maps every KNOWN field key onto its RHF field via
 * `setError(field, { type: 'server', message })`; anything unmapped is returned as `globalMessage`
 * for the form-level `MessageBar` — never a raw toast for a field-level error.
 *
 * Single canonical copy — supersedes the per-feature `mapServerErrorToFormState` duplicates that
 * predated this scaffold. Each of F031/F032/F039 previously kept its own copy "so features stay
 * decoupled"; now that a shared form scaffold exists, this is the "primitive used in >= 2 features
 * MUST be promoted" case (Charter M1). F031's Agent Registry forms are out of this migration's
 * scope and keep their own copy for now — see the final report.
 */
interface ServerErrorShape {
  message?: string;
  details?: Record<string, string[] | string>;
}

export function applyServerErrors<TFieldValues extends FieldValues>(
  error: unknown,
  knownFields: readonly string[],
  setError: UseFormSetError<TFieldValues>
): { globalMessage: string | undefined } {
  let globalMessage: string | undefined;
  let mappedAny = false;

  const shaped = error as ServerErrorShape;
  const details = shaped?.details;
  if (details && typeof details === 'object') {
    for (const [key, val] of Object.entries(details)) {
      // Newtonsoft's default PascalCase output (`ServerName`) -> camelCase field key (`serverName`).
      const camelKey = key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
      const message = Array.isArray(val) ? val.join(' ') : String(val);
      if (knownFields.includes(camelKey)) {
        mappedAny = true;
        // reason: knownFields is the caller-declared, runtime-verified subset of TFieldValues keys;
        // Path<TFieldValues> can't be derived from a plain string[] at the type level.
        setError(camelKey as Path<TFieldValues>, { type: 'server', message });
      } else {
        globalMessage = globalMessage ? `${globalMessage} ${message}` : message;
      }
    }
  }

  if (!mappedAny && !globalMessage) {
    globalMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  }

  return { globalMessage };
}
